import { type Mat4 } from '@/math/mat4';
import { type Vec3 } from '@/math/vec3';
import { type GpuMesh } from './mesh-gpu';

export const INSTANCE_FLOATS = 24;
export const INSTANCE_STRIDE_BYTES = INSTANCE_FLOATS * 4;
const STATIC_STRIDE_BYTES = 32;

export interface InstanceBatch {
  vao: WebGLVertexArrayObject;
  instanceVbo: WebGLBuffer;
  indexCount: number;
  indexType: number;
  capacity: number;
  count: number;
  cpu: Float32Array;
}

export const createInstanceBatch = (
  gl: WebGL2RenderingContext,
  mesh: GpuMesh,
  capacity: number,
): InstanceBatch => {
  const vao = gl.createVertexArray();
  const instanceVbo = gl.createBuffer();
  if (vao === null || instanceVbo === null) throw new Error('instance batch alloc failed');

  gl.bindVertexArray(vao);

  // static attributes from the source mesh (locations 0-2).
  gl.bindBuffer(gl.ARRAY_BUFFER, mesh.vbo);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 3, gl.FLOAT, false, STATIC_STRIDE_BYTES, 0);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 3, gl.FLOAT, false, STATIC_STRIDE_BYTES, 12);
  gl.enableVertexAttribArray(2);
  gl.vertexAttribPointer(2, 2, gl.FLOAT, false, STATIC_STRIDE_BYTES, 24);

  // per-instance attributes (locations 3-8), divisor=1.
  gl.bindBuffer(gl.ARRAY_BUFFER, instanceVbo);
  gl.bufferData(gl.ARRAY_BUFFER, capacity * INSTANCE_STRIDE_BYTES, gl.DYNAMIC_DRAW);
  // mat4 model: four vec4s at locations 3-6, byte offsets 0/16/32/48
  for (let col = 0; col < 4; col++) {
    const loc = 3 + col;
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 4, gl.FLOAT, false, INSTANCE_STRIDE_BYTES, col * 16);
    gl.vertexAttribDivisor(loc, 1);
  }
  // uvRect at 7, tint(+pad) at 8
  gl.enableVertexAttribArray(7);
  gl.vertexAttribPointer(7, 4, gl.FLOAT, false, INSTANCE_STRIDE_BYTES, 64);
  gl.vertexAttribDivisor(7, 1);
  gl.enableVertexAttribArray(8);
  gl.vertexAttribPointer(8, 4, gl.FLOAT, false, INSTANCE_STRIDE_BYTES, 80);
  gl.vertexAttribDivisor(8, 1);

  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, mesh.ebo);
  gl.bindVertexArray(null);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);

  return {
    vao,
    instanceVbo,
    indexCount: mesh.indexCount,
    indexType: mesh.indexType,
    capacity,
    count: 0,
    cpu: new Float32Array(capacity * INSTANCE_FLOATS),
  };
};

export const setInstance = (
  batch: InstanceBatch,
  index: number,
  model: Mat4,
  uvRect: Float32Array,
  tint: Vec3,
): void => {
  const base = index * INSTANCE_FLOATS;
  const cpu = batch.cpu;
  for (let i = 0; i < 16; i++) cpu[base + i] = model[i]!;
  cpu[base + 16] = uvRect[0]!;
  cpu[base + 17] = uvRect[1]!;
  cpu[base + 18] = uvRect[2]!;
  cpu[base + 19] = uvRect[3]!;
  cpu[base + 20] = tint[0]!;
  cpu[base + 21] = tint[1]!;
  cpu[base + 22] = tint[2]!;
  cpu[base + 23] = 0;
};

export const uploadInstances = (
  gl: WebGL2RenderingContext,
  batch: InstanceBatch,
  count: number,
): void => {
  if (count > batch.capacity) throw new Error('instance count exceeds batch capacity');
  batch.count = count;
  gl.bindBuffer(gl.ARRAY_BUFFER, batch.instanceVbo);
  gl.bufferSubData(gl.ARRAY_BUFFER, 0, batch.cpu, 0, count * INSTANCE_FLOATS);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
};

export const disposeInstanceBatch = (
  gl: WebGL2RenderingContext,
  batch: InstanceBatch,
): void => {
  gl.deleteVertexArray(batch.vao);
  gl.deleteBuffer(batch.instanceVbo);
};
